import React, { useState, useEffect } from 'react';
import { categoriesAPI } from '../../utils/api';
import { LoadingSpinner } from '../../components/common';

const EMPTY_FORM = { name: '', slug: '', icon: '', description: '', sort_order: 0, is_active: 1 };

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [sortCol, setSortCol] = useState('sort_order');
  const [sortDir, setSortDir] = useState('asc');

  const load = () => categoriesAPI.getAll().then(r => setCategories(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const slugify = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const openNew = () => { setForm(EMPTY_FORM); setEditing(null); setShowForm(true); setError(''); };
  const openEdit = (c) => { setForm({ ...c }); setEditing(c.id); setShowForm(true); setError(''); };

  const handleSave = async () => {
    if (!form.name || !form.slug) { setError('Name and Slug are required'); return; }
    setSaving(true); setError('');
    try {
      if (editing) await categoriesAPI.update(editing, form);
      else await categoriesAPI.create(form);
      setShowForm(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deactivate this category?')) return;
    await categoriesAPI.delete(id);
    load();
  };

  const toggleSort = (col) => { setSortDir(sortCol === col && sortDir === 'asc' ? 'desc' : 'asc'); setSortCol(col); };
  const si = (col) => <span style={{ fontSize: '0.65rem', marginLeft: 2, opacity: sortCol === col ? 1 : 0.3 }}>{sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '↕'}</span>;
  const sortedCategories = [...categories].sort((a, b) => {
    const va = a[sortCol] ?? ''; const vb = b[sortCol] ?? '';
    const n = parseFloat(va), m = parseFloat(vb);
    const cmp = !isNaN(n) && !isNaN(m) ? n - m : String(va).toLowerCase().localeCompare(String(vb).toLowerCase());
    return sortDir === 'desc' ? -cmp : cmp;
  });

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div><h1>Categories</h1><p>{categories.length} categories</p></div>
        <button className="btn btn-primary" onClick={openNew}>+ Add Category</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: '1.5rem', borderLeft: '4px solid #2563eb' }}>
          <h2 style={{ fontWeight: 700, marginBottom: '1rem' }}>{editing ? 'Edit Category' : 'New Category'}</h2>
          {error && <div className="alert alert-error">{error}</div>}
          <div className="grid-3">
            <div className="form-group">
              <label className="form-label">Name *</label>
              <input
                className="form-input"
                value={form.name}
                onChange={e => {
                  set('name', e.target.value);
                  if (!editing) set('slug', slugify(e.target.value));
                }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Slug *</label>
              <input className="form-input" value={form.slug} onChange={e => set('slug', slugify(e.target.value))} placeholder="auto-generated" />
            </div>
            <div className="form-group">
              <label className="form-label">Icon (emoji)</label>
              <input className="form-input" value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="📚" maxLength={4} />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Sort Order</label>
              <input className="form-input" type="number" value={form.sort_order} onChange={e => set('sort_order', parseInt(e.target.value) || 0)} />
            </div>
            {editing && (
              <div className="form-group">
                <label className="form-label">Status</label>
                <select className="form-select w-full" value={form.is_active} onChange={e => set('is_active', e.target.value)}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} />
          </div>
          <div className="flex gap-2" style={{ marginTop: '0.5rem' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            <button className="btn btn-outline" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <LoadingSpinner /> : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Icon</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('name')}>Name {si('name')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('slug')}>Slug {si('slug')}</th>
                  <th>Description</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('sort_order')}>Sort {si('sort_order')}</th>
                  <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('is_active')}>Status {si('is_active')}</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedCategories.map(c => (
                  <tr key={c.id}>
                    <td style={{ fontSize: '1.4rem' }}>{c.icon || '—'}</td>
                    <td style={{ fontWeight: 600 }}>{c.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6b7280' }}>{c.slug}</td>
                    <td style={{ fontSize: '0.82rem', color: '#6b7280', maxWidth: 200 }}>{c.description || '—'}</td>
                    <td>{c.sort_order}</td>
                    <td>
                      <span className={`badge ${c.is_active ? 'badge-success' : 'badge-danger'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn btn-outline btn-sm" onClick={() => openEdit(c)}>Edit</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(c.id)}>Del</button>
                      </div>
                    </td>
                  </tr>
                ))}
                {categories.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>No categories yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
